import React, { useState } from "react";
import { Cell, HeaderGroup } from "react-table";
import { Box, Skeleton, TableCell } from "@mui/material";
import { muiTheme } from "@/styles/theme";
import { UserTag } from "@/modules/user";
import { filterRows } from "@/modules/common/utils";
import { TableRowProps } from "@/modules/common/components/tables/common-table/body/types";
import { EnvironmentVariables } from "@/modules/common/types";
import Checkbox from "@/modules/leads/components/leads-table/components/body/components/checkbox";

function getNestedProperty(obj: Record<string, any>, path: string): any {
  return path.split(".").reduce((acc, part) => {
    if (acc === null || acc === undefined) return acc;
    // Handle array indices by checking if the part is a number
    const arrayMatch = part.match(/(\w+)\[(\d+)\]/);
    if (arrayMatch) {
      const [, arrayName, index] = arrayMatch;
      return acc[arrayName]?.[parseInt(index, 10)];
    }
    return acc[part];
  }, obj);
}

const TableRow: React.FC<TableRowProps> = ({
  rows,
  prepareRow,
  headerGroups,
  searchValue,
  clickableRows,
  isLoading,
  onFilter,
  getRowBackgroundColor,
  selectedColoredRow,
  enableRowSelection,
  extraColumnWidth,
  minTableWidth,
  redirectionAccessor,
  redirectionUrl,
}) => {
  const filteredRows = filterRows(rows, searchValue);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  const handleRowClick = (link: string) => {
    clickableRows && window.open(link, "_blank", "noopener,noreferrer");
  };

  if (onFilter) {
    onFilter(filteredRows.length);
  }

  const maskPhoneNumber = (phoneNumber: string, isHovered: boolean) => {
    if (!phoneNumber) return phoneNumber;
    if (isHovered) return phoneNumber;
    return phoneNumber.slice(0, -3) + "***";
  };

  return (
    <>
      {filteredRows.map((row: any) => {
        prepareRow(row);
        const isRowHovered = hoveredRowId === row.id;

        return (
          <Box
            {...row.getRowProps()}
            key={row.id}
            onMouseEnter={() => setHoveredRowId(row.id)}
            onMouseLeave={() => setHoveredRowId(null)}
            sx={{
              display: "flex",
              zIndex: 1,
              backgroundColor:
                getRowBackgroundColor &&
                getRowBackgroundColor!(row, selectedColoredRow)
                  ? getRowBackgroundColor!(row, selectedColoredRow)
                  : muiTheme.colorSchemes.light.palette.background.white,
              "&:nth-of-type(odd)": {
                backgroundColor:
                  getRowBackgroundColor &&
                  getRowBackgroundColor!(row, selectedColoredRow)
                    ? getRowBackgroundColor!(row, selectedColoredRow)
                    : muiTheme.colorSchemes.light.palette.background.blue1,
              },
            }}
          >
            {enableRowSelection && (
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                  backgroundColor: "background.white",
                  borderBottom: "1px solid #deebf4",
                  boxShadow: "2px 0 2px 0 rgba(0, 0, 0, 0.2)",
                  width: 50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Checkbox row={row} />
              </TableCell>
            )}
            <Box
              sx={{
                padding: "8px 0",
                cursor: clickableRows ? "pointer" : "",
                display: "flex",
                minWidth: minTableWidth,
                "&:hover": {
                  backgroundColor: "#d5eaf2",
                },
              }}
              onClick={() =>
                handleRowClick(
                  redirectionUrl
                    ? `${redirectionUrl}/${
                        redirectionAccessor
                          ? getNestedProperty(
                              row?.original,
                              redirectionAccessor
                            )
                          : row?.original?.uid || row?.original?.id
                      }`
                    : `${EnvironmentVariables.CRM_NAVIGATE_URL}/leads/${
                        redirectionAccessor
                          ? getNestedProperty(
                              row?.original,
                              redirectionAccessor
                            )
                          : row?.original?.nanoId
                      }`
                )
              }
            >
              {row.cells.map((cell: Cell, index: number) => {
                const matchingColumn: HeaderGroup<object> & {
                  minWidth?: number;
                  rowCellStyles?: object;
                  hideColumn?: boolean;
                } = headerGroups[0].headers[index];

                return (
                  !matchingColumn.hideColumn && (
                    <TableCell
                      {...cell.getCellProps()}
                      key={index}
                      sx={{
                        minWidth: `${
                          matchingColumn.minWidth &&
                          matchingColumn.minWidth + extraColumnWidth
                        }px`,
                        padding: "0 14px",
                        textAlign: "left",
                        fontWeight: 400,
                        color: "#313131",
                        position: "relative",
                        border: "none",
                        "&:not(:last-child)": {
                          borderRight: `1px solid ${muiTheme.colorSchemes.light.palette.neutral[200]}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          ...matchingColumn.rowCellStyles,
                        }}
                      >
                        {cell.column.Header === "Agent" ? (
                          <UserTag
                            name={cell.value.name}
                            color={cell.value.color}
                          />
                        ) : cell.column.Header === "Telephone" ? (
                          (maskPhoneNumber(
                            cell.value,
                            isRowHovered
                          ) as React.ReactNode)
                        ) : (
                          (cell.render("Cell") as React.ReactNode)
                        )}
                      </Box>
                    </TableCell>
                  )
                );
              })}
            </Box>
          </Box>
        );
      })}

      {isLoading && rows.length === 0 && (
        <Box
          sx={{
            display: "flex",
            maxWidth: minTableWidth,
          }}
        >
          {enableRowSelection && (
            <Box
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 2,
                backgroundColor: "#FFF",
                borderBottom: "1px solid #E6F0F4",
                padding: "8px",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "48px",
              }}
            />
          )}
          {headerGroups[0]?.headers?.map((column, index) => (
            <TableCell
              sx={{
                justifyContent: "start",
                alignItems: "center",
                minWidth: `${
                  column.minWidth && column.minWidth + extraColumnWidth
                }px`,
                maxWidth: `${
                  column.minWidth && column.minWidth + extraColumnWidth
                }px`,
                width: `${
                  column.minWidth && column.minWidth + extraColumnWidth
                }px`,
              }}
              key={index}
            >
              {Array.from({ length: 15 }).map((_, skeletonIndex) => (
                <Skeleton
                  key={skeletonIndex}
                  variant="text"
                  sx={{
                    minWidth: `${
                      column.minWidth && column.minWidth + extraColumnWidth - 28
                    }px`,
                    maxWidth: `${
                      column.minWidth && column.minWidth + extraColumnWidth - 28
                    }px`,
                    width: `${
                      column.minWidth && column.minWidth + extraColumnWidth - 28
                    }px`,
                    borderRadius: 1,
                  }}
                  height={54}
                />
              ))}
            </TableCell>
          ))}
        </Box>
      )}
    </>
  );
};

export default TableRow;
